import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Button, FormHelperText, Stack, Typography } from '@mui/material'
import { Add } from '@mui/icons-material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { IFileUploadSetting } from '../../../../shared/services/file-upload-setting/file-upload-setting.model';
import ImportMappingService from '../../../../shared/services/import-mapping/import-mapping.service';
import FileUploadSettingService from '../../../../shared/services/file-upload-setting/file-upload-setting.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import RuleService from '../../../../shared/services/rule/rule.service';
import { FormTextInputDisplay } from '../../form-field-components/new-and-edit/form-text-input';
import { ImportMappingSupplierFormField } from './import-mapping-supplier-form-field';

export const ImportMappingSupplierForm: FunctionComponent<{
  fileUploadSetting: IFileUploadSetting
  getFileUploadSettings: (contactSupplierId: string) => void
}> = ({
  fileUploadSetting,
  getFileUploadSettings
}) => {
    //Services
    const importMappingService = new ImportMappingService()
    const fileUploadSettingService = new FileUploadSettingService()
    const userNotificationsContext = useContext(UserNotificationsContext)
    const ruleService = new RuleService()
    //State
    const initialMappedSelections = new Map(
      fileUploadSetting.mappings.importMappings.map(im => [im.id, im.mappedHeading])
    );
    const [hasRules, setHasRules] = useState<boolean>(false)
    const [mappedSelections, setMappedSelections] = useState(initialMappedSelections);
    const [editField, setEditField] = useState<string>('')
    const [errorCustom, setErrorCustom] = useState<{ id: string, error: string } | undefined>()
    const [errorMapped, setErrorMapped] = useState<{ id: string, error: string } | undefined>()
    const [addNewErrorCustom, setAddNewErrorCustom] = useState<string | undefined>()
    const [addNewErrorMapped, setAddNewErrorMapped] = useState<string | undefined>()
    const [addNewHeading, setAddNewHeading] = useState<boolean>(false)
    //Use Effects
    useEffect(() => {
      ruleService.getAll()
        .then(response => {
          if (response.length > 0) {
            setHasRules(true)
          }
        })
        .catch(err => console.log(err))
    }, [])
    //Formik
    const fileUploadSettingSchema = Yup.object().shape({
      sheetNumber: Yup.number(),
      headingRow: Yup.number(),
      mappingMethod: Yup.string(),
    })
    const formik = useFormik<any>({
      initialValues: {
        sheetNumber: fileUploadSetting.sheetNumber,
        headingRow: fileUploadSetting.headingRow,
        mappingMethod: fileUploadSetting.mappingMethod,
      },
      validationSchema: fileUploadSettingSchema,
      onSubmit: (values) => {
        fileUploadSettingService.edit(fileUploadSetting.id, values)
          .then(() => {
            cleanUpHandler()
            userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
            getFileUploadSettings(fileUploadSetting.contactSupplierId)
          })
          .catch(err => {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            console.log(err)
          })
      }
    });

    const editHandler = (importMappingId: string) => {
      setEditField(importMappingId)
    }

    const submitEditHandler = (importMappingId: string, customHeading: string, mappedHeading: string) => {
      if (!customHeading) {
        setErrorCustom({ id: importMappingId, error: 'Custom Heading is required' })
        return
      }
      if (!mappedHeading) {
        setErrorMapped({ id: importMappingId, error: 'Mapped System Field is required' })
        return
      }
      const importMapping = {
        id: importMappingId,
        customHeading: customHeading,
        mappedHeading: mappedHeading,
        fileUploadSettingId: fileUploadSetting.id
      }
      importMappingService.edit(importMapping.id, importMapping)
        .then(() => {
          cleanUpHandler()
          userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          console.log(errorMapped)
        })
    }

    const submitCreateHandler = (customHeading: string, mappedHeading: string) => {
      setAddNewErrorCustom(undefined)
      setAddNewErrorMapped(undefined)
      if (!customHeading) {
        setAddNewErrorCustom('Custom Heading is required')
        return
      }
      if (!mappedHeading) {
        setAddNewErrorMapped('Mapped System Field is required')
        return
      }
      const importMapping = {
        customHeading: customHeading,
        mappedHeading: mappedHeading,
        fileUploadSettingId: fileUploadSetting.id
      }
      importMappingService.create(importMapping)
        .then(() => {
          cleanUpHandler()
          getFileUploadSettings(fileUploadSetting.contactSupplierId)
          userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        })
        .catch(err => {
          userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          console.log(errorMapped)
        })
    }

    const deleteHandler = (importMappingId: string) => {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Confirmation',
        message: 'Are you sure you want to delete this column heading? This action cannot be undone.',
        buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
      })
        .then(response => {
          if (response) {
            importMappingService.delete(importMappingId)
              .then(() => {
                getFileUploadSettings(fileUploadSetting.contactSupplierId)
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
              })
          }
        })
    }

    const cancelEditHandler = () => {
      formik.setValues({
        sheetNumber: fileUploadSetting.sheetNumber,
        headingRow: fileUploadSetting.headingRow,
        mappingMethod: fileUploadSetting.mappingMethod,
      })
      cleanUpHandler()
    }

    const cleanUpHandler = () => {
      setEditField('')
      setErrorCustom(undefined)
      setErrorMapped(undefined)
      setAddNewErrorCustom(undefined)
      setAddNewErrorMapped(undefined)
      setAddNewHeading(false)
    }

    return (
      <Stack>
        <Typography variant='subtitle1' sx={{ mb: 1 }}>{fileUploadSetting.fileType === 'emissions' ? 'Activity-Based Import Mapping' : ''}</Typography>
        <Box component='form' onSubmit={formik.handleSubmit}>
          {/* {fileUploadSetting.mimetype !== 'text/csv' && */}
          {/* <> */}
          <FormTextInputDisplay
            isTopRow={true}
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            hideEditIcon={addNewHeading}
            disabled={editField !== 'sheetNumber'}
            title='Sheet Number'
            name='sheetNumber'
            value={formik.values.sheetNumber}
            touched={formik.touched.sheetNumber}
            errors={formik.errors.sheetNumber}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            type='number'
          />
          <FormTextInputDisplay
            isEditPage={true}
            editField={editField}
            setEditField={setEditField}
            hideEditIcon={addNewHeading}
            disabled={editField !== 'headingRow'}
            title='Row Number of Headings'
            name='headingRow'
            isBottomRow={true}
            value={formik.values.headingRow}
            touched={formik.touched.headingRow}
            errors={formik.errors.headingRow}
            onChange={formik.handleChange}
            cancelEditHandler={cancelEditHandler}
            type='number'
          />
          {/* </>} */}
          {/* <FormRadioInputDisplay
            isTopRow={fileUploadSetting.mimetype === 'text/csv' ? true : false}
            isEditPage={true}
            editField={editField}
            hideEditIcon={addNewHeading || !hasRules}
            setEditField={setEditField}
            cancelEditHandler={cancelEditHandler}
            name='mappingMethod'
            disabled={editField !== 'mappingMethod'}
            title='Emission Mapping Mode'
            value={formik.values.mappingMethod}
            touched={formik.touched.mappingMethod}
            errors={formik.errors.mappingMethod}
            onChange={formik.handleChange}
            optionOneLabel='Manual Hierarchy (Group > Category > Level 2-5)'
            optionOneValue={'manual'}
            optionTwoValue={'rule'}
            optionTwoLabel='Custom Rule'
            isBottomRow={true}
          /> */}
        </Box>
        <Stack sx={{ mt: 2 }}>
          {fileUploadSetting.mappings.importMappings.map((importMapping, index) => {
            return (
              <ImportMappingSupplierFormField
                key={index}
                editHandler={editHandler}
                deleteHandler={deleteHandler}
                submitEditHandler={submitEditHandler}
                closeHandler={cleanUpHandler}
                id={importMapping.id}
                editField={editField}
                customHeading={importMapping.customHeading}
                mappedHeading={importMapping.mappedHeading}
                options={fileUploadSetting.mappings.options}
                errorCustom={errorCustom}
                errorMapped={errorMapped}
                mappedSelections={mappedSelections}
                setMappedSelections={setMappedSelections}
                addNewHeading={addNewHeading}
              />
            )
          })}
          <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant='text' onClick={() => setAddNewHeading(true)} disabled={addNewHeading || editField !== ''}>
              <Add />
              <Typography>Add New Heading</Typography>
            </Button>
          </Stack>
          {addNewHeading &&
            <ImportMappingSupplierFormField
              editHandler={editHandler}
              deleteHandler={deleteHandler}
              submitCreateHandler={submitCreateHandler}
              closeHandler={cleanUpHandler}
              customHeading={''}
              mappedHeading={''}
              options={fileUploadSetting.mappings.options}
              errorCustom={errorCustom}
              errorMapped={errorMapped}
              mappedSelections={mappedSelections}
              setMappedSelections={setMappedSelections}
              setAddNewHeading={setAddNewHeading}
              addNewHeading={addNewHeading}
            />
          }
          {addNewErrorCustom &&
            <Stack>
              <FormHelperText sx={{ color: '#bf3333', marginLeft: "16px !important" }}>{addNewErrorCustom}</FormHelperText>
            </Stack>}
          {addNewErrorMapped &&
            <Stack sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignContent: 'center', ml: 8 }}>
              <FormHelperText sx={{ color: '#bf3333', marginLeft: "16px !important" }}>{addNewErrorMapped}</FormHelperText>
            </Stack>}
        </Stack>
      </Stack >
    )
  }